import { Link } from "@tanstack/react-router";
import { Bell, BellOff, Send } from "lucide-react";
import { toast } from "sonner";

import { QueryState, describeError } from "@/components/query-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { NotificationState } from "@/generated/api/model";
import {
    useGetNotifications,
    useResetNotifyThresholds,
    useSendTestNotification,
    useSetNotifyThresholds,
} from "@/generated/api/opportunities/opportunities";

import { ada, chainTime, duration, useInvalidateOpportunities } from "./format";
import { OverrideBadge, ThresholdsForm } from "./ThresholdsCard";

/**
 * Phone alerts: whether they are on, where they go, how the last delivery went, and their own
 * thresholds, separate from what the statistics count as actionable.
 * [[spec-phone-notification-when-an-actionable-arbitrage-opportunity-opens]]
 */
export function NotificationsCard() {
    const notifications = useGetNotifications();
    const invalidate = useInvalidateOpportunities();
    const onSettled = { onSuccess: () => invalidate(), onError: (error: unknown) => toast.error(describeError(error)) };
    const save = useSetNotifyThresholds({ mutation: onSettled });
    const reset = useResetNotifyThresholds({ mutation: onSettled });
    const test = useSendTestNotification({
        mutation: {
            onSuccess: () => {
                toast.success("Test notification sent");
                void invalidate();
            },
            onError: (error: unknown) => {
                toast.error(describeError(error));
                void invalidate();
            },
        },
    });

    return (
        <Card role="region" aria-labelledby="alerts-heading">
            <CardHeader>
                <h2 id="alerts-heading" className="font-semibold">
                    Phone alerts
                </h2>
                {notifications.data && <OnOffBadge enabled={notifications.data.enabled} />}
            </CardHeader>
            <CardContent className="space-y-4">
                <QueryState query={notifications} isEmpty={() => false} emptyMessage="">
                    {(data) => (
                        <>
                            <Delivery state={data} />
                            <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <h3 className="text-sm font-medium">Alert when</h3>
                                    <OverrideBadge state={data.thresholds} />
                                </div>
                                <ThresholdsForm
                                    key={JSON.stringify(data.thresholds.effective)}
                                    idPrefix="notify"
                                    state={data.thresholds}
                                    summary={(t) => (
                                        <>
                                            A window has been open for at least{" "}
                                            <span className="text-foreground">{duration(t.min_duration_secs)}</span> and
                                            is worth at least{" "}
                                            <span className="text-foreground">{ada(t.min_profit_lovelace)} ADA</span>{" "}
                                            right now. A follow-up goes out when it closes or drops below.
                                        </>
                                    )}
                                    pending={save.isPending || reset.isPending}
                                    onSave={(thresholds) => save.mutate({ data: thresholds })}
                                    onReset={() => reset.mutate()}
                                />
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={!data.enabled || test.isPending}
                                onClick={() => test.mutate()}
                            >
                                <Send />
                                Send test notification
                            </Button>
                        </>
                    )}
                </QueryState>
            </CardContent>
        </Card>
    );
}

function OnOffBadge({ enabled }: { enabled: boolean }) {
    return enabled ? (
        <Badge variant="success">
            <Bell className="size-3.5" />
            on
        </Badge>
    ) : (
        <Badge>
            <BellOff className="size-3.5" />
            off
        </Badge>
    );
}

function Delivery({ state }: { state: NotificationState }) {
    if (!state.enabled || !state.channel) {
        return (
            <p className="text-sm text-muted-foreground">
                Alerts are switched off on the bot. Set <code className="break-all">ARB_NOTIFY_ENABLED=true</code> to
                turn them on.
            </p>
        );
    }
    return (
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Channel</dt>
            <dd className="break-all">
                {state.channel.kind} · {state.channel.target}
            </dd>
            <dt className="text-muted-foreground">Last sent</dt>
            <dd>{state.last_sent_at == null ? "never" : chainTime(state.last_sent_at)}</dd>
            <dt className="text-muted-foreground">Delivered</dt>
            <dd className="tabular-nums">
                {state.sent} sent · {state.failed} failed
            </dd>
            {state.last_error && (
                <>
                    <dt className="text-muted-foreground">Last error</dt>
                    <dd className="break-words text-destructive">
                        {state.last_error}
                        {state.last_error_at != null && (
                            <span className="text-muted-foreground"> · {chainTime(state.last_error_at)}</span>
                        )}
                    </dd>
                </>
            )}
        </dl>
    );
}

/** One line on the Stats overview: whether alerts are on and what triggers them. */
export function NotificationsSummary() {
    const notifications = useGetNotifications();
    const data = notifications.data;
    if (!data) return null;
    const { min_duration_secs, min_profit_lovelace } = data.thresholds.effective;

    return (
        <p
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground"
            aria-label="Phone alerts"
        >
            <span>Phone alerts</span>
            <OnOffBadge enabled={data.enabled} />
            {data.enabled && (
                <span>
                    ≥ {ada(min_profit_lovelace)} ADA, open ≥ {duration(min_duration_secs)}
                </span>
            )}
            <Link to="/settings" className="underline underline-offset-4">
                Settings
            </Link>
        </p>
    );
}
